import { useForm } from "react-hook-form";
import humanImage from "../../TDD-Images/home.png";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { useAppContext } from "../appContext";
import { Input } from "./ui/input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  dailyTimeOptions,
  dayOptions,
  discoveryOptions,
  educationLevel,
  genderOptions,
  identityOptions,
  raceOptions,
  roleOptions,
  timeSpentOptions,
  visaOptions,
  wantedRoles,
  workExperienceOptions,
} from "@/utils/utils";
import FormBadge from "./FormBadge";
import FormSelect from "./FormSelect";
import FormComboBox from "./FormComboBox";
import { Button } from "./ui/button";
import { UNIVERSITIES } from "./universityList";
import { useMemo, useState } from "react";
import { cn } from "@/utils/utils";
import { Layers2 } from "lucide-react";
import UploadFile from "./UploadFile";
import { useRef } from "react";
import { updateIntakeForm, uploadUserResume } from "@/api";
import { isAdmin} from "@/utils/utils";
import { LogoutButton } from "./signin";

const IntakeForm = () => {

  const {
    auth0,
    state,
    reducers,
  } = useAppContext();

  const isAdminUser : boolean = isAdmin(state.profileUser); // Assuming this is how we check for admin

  const buttonRef = useRef<HTMLButtonElement>(null);
  const steps =  isAdminUser ? ["STEP-1"] : ["STEP-1", "STEP-2", "STEP-3", "STEP-4"];
  const [step, setStep] = useState(0);
  const [resume, setResume] = useState<{ text: string | undefined; file: string | undefined }>({
    text: "",
    file: "",
  });

  const formSchema = z.object({
    firstName: z.string().min(2, { message: "First name is required" }),
    lastName: z.string().min(2, { message: "Last name is required" }),
    gender: z.string().min(2, { message: "Please select at least one" }),
    race: z.array(z.string(), { message: "Please select at least one" }),
    discovery: z.string().min(2, { message: "Please select one" }),
    referral: z.string(),
    identity: z.array(z.string()),
    educationLevel: z
      .string()
      .min(2, { message: "Please select at least one" }),
    university: z.string().min(2, { message: "Please select one" }),
    workExp: z.array(z.string()),
    interestedRoles: z.array(z.string()),
    wantedRoles: z.array(z.string()),
    visaOpts: z.string(),
    timeSpent: z.string(),
    goal: z.string(),
    dayOptions: z.array(z.string()),
    dailyTime: z.string(),
    linkedIn: z.string(),
  });

  const adminFormSchema = z.object({
    firstName: z.string().min(2, { message: "First name is required" }),
    lastName: z.string().min(2, { message: "Last name is required" }),
    gender: z.string().min(2, { message: "Please select at least one" }),
    race: z.array(z.string(), { message: "Please select at least one" }),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      gender: "",
      race: [],
      discovery: "",
      referral: "",
      identity: [],
      educationLevel: "",
      university: "",
      workExp: [],
      interestedRoles: [],
      wantedRoles: [],
      visaOpts: "",
      timeSpent: "",
      goal: "",
      dayOptions: [],
      dailyTime: "",
      linkedIn: "",
    },
  });

  const adminForm = useForm<z.infer<typeof adminFormSchema>>({
    resolver: zodResolver(adminFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      gender: "",
      race: [],
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    let user2 = await updateIntakeForm(values);
    let user3;
    if(resume.text && resume.text != "" && resume.file && resume.file != ""){
      user3 =await uploadUserResume(resume.text, resume.file);
    }
    reducers.setprofileUser(user3||user2);

  };

  const onAdminSubmit = async(values: z.infer<typeof adminFormSchema>) => {
      // For admin, only submit the basic info
      const adminValues = {
        firstName: values.firstName,
        lastName: values.lastName,
        gender: values.gender,
        race: values.race,
        interestedRoles: ["Business Administrator"],
      };
      let user2 = await updateIntakeForm(adminValues);
      reducers.setprofileUser(user2);
  };

  const validateStep = async () => {
    let isValid = false;
    
    if (steps[step] === "STEP-1") {
      isValid = await form.trigger([
        "firstName",
        "lastName",
        "gender",
        "discovery",
        "race",
        "identity",
        "educationLevel",
        "university",
        "workExp"
      ]);
    } 
    else if (steps[step] === "STEP-2") {
      isValid = await form.trigger([
        "interestedRoles",
        "wantedRoles",
        "visaOpts",
        "timeSpent"
      ]);
    }
    else if (steps[step] === "STEP-3") {
      isValid = await form.trigger([
        "dayOptions",
        "dailyTime"
      ]);
    }

    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateStep();
    if (isValid) {
      setStep((s) => (s < steps.length - 1 ? s + 1 : s));
    }
    // If not valid, form errors will be shown automatically
  };

  const handleBack = () => {
    setStep((s) => (s > 0 ? s - 1 : s));
  };

  const header = useMemo(() => {
    if (steps[step] === "STEP-1") return "Tell us about yourself";
    if (steps[step] === "STEP-2") return "Tell us about your career interests";
    if (steps[step] === "STEP-3")
      return "Tell us about your practice preferences";
    if (steps[step] === "STEP-4") return "Share your experience with us";
    return "Tell us about your career goals";
  }, [step]);

  return (
<>
       {!state.profileUser?.email_verified &&
                    <div className="flex-col justify-center p-4">
                    <h3>Email Verification Step</h3>
                        <p>{`Please verify your email by clicking the verification link sent to `} <b>{state.profileUser?.email}</b>.
                        <br/>
                        Click the button below once you have verified your email to continue.
                        <br/>
                        Be sure to check your junk folder.</p>

                        <div className="settingsButtons2 mb-4">
                            <div className = "formexperiencebutton3" onClick={()=>auth0.loginWithPopup()}>I'm Verified!</div>
                        </div>
                        <LogoutButton />
                        {/* {canResend && <button onClick={()=>{resendVerificationEmail(props.email);setcanResend(false);}} className="formexperiencebutton-intake">Resend Verification Email</button>} */}
                    </div>
                }
    {state.profileUser?.email_verified &&
    <div className="flex p-4 pb-0 full-nav relative">
      <div
        className={cn(
          "fixed bottom-0 left-0 bg-white flex justify-between items-center w-full z-50 px-4 py-2 drop-shadow-md border-t",
          steps[step] == "STEP-1" && "justify-end"
        )}
      >
        {!isAdminUser && steps[step] !== "STEP-1" && (
          <Button
            className="bg-white text-black shadow-none border-[#D5D7DA] border"
            onClick={handleBack}
          >
            Back
          </Button>
        )}

        {isAdminUser || steps[step] === "STEP-4" ? (
          <Button
            className="bg-[#FFC99E] text-black shadow-none border-[#D5D7DA] hover:bg-[#FFC99E]"
            type="submit"
            onClick={() => buttonRef.current?.click()}
          >
            Submit
          </Button>
        ) : (
          <Button
            className="bg-[#FFC99E] text-black shadow-none border-[#D5D7DA] hover:bg-[#FFC99E]"
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </div>
      <div className="h-full">
        <img
          src={humanImage}
          alt="Human"
          className="h-full object-cover md:hidden sm:hidden lg:min-w-[500px] lg:block xl:min-w-[500px] xl:block"
        />
      </div>
      <div className="py-4 px-8 w-full h-full overflow-y-auto">
        <div className="flex flex-col gap-4 w-full">
          <h2 className="font-semibold text-black text-left p-0 m-0 text-[24px]">
            {header}
          </h2>
          {
            isAdminUser &&   <Form {...adminForm}>
            <form onSubmit={adminForm.handleSubmit(onAdminSubmit)} className="gap-8">
              {steps[step] === "STEP-1" && (
                <>
                  <div className="flex gap-4">
                    <FormField
                      control={adminForm.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem className="w-1/2 shadow-none drop-shadow-none">
                          <FormLabel className="m-0 p-0 text-[14px] text-[#6F7582]">
                            First Name
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Type here" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={adminForm.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem className="w-1/2 shadow-none drop-shadow-none">
                          <FormLabel className="m-0 p-0 text-[14px] text-[#6F7582]">
                            Last Name
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Type here" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={adminForm.control}
                      name="gender"
                      render={({ field }) => (
                        <FormBadge
                          label="What is your gender identity?"
                          options={genderOptions}
                          field={field}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={adminForm.control}
                      name="race"
                      render={({ field }) => (
                        <FormSelect
                          label="What is your race? Select all that apply."
                          field={field}
                          placeholder="Select all that apply"
                          options={raceOptions}
                        />
                      )}
                    />
                  </div>
                  <Button ref={buttonRef} type="submit" className="hidden">
                    Submit
                  </Button>
                </>
              )}
            </form>
          </Form>
          }
          {!isAdminUser &&
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="gap-8">
              {steps[step] === "STEP-1" && (
                <>
                  <div className="flex gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem className="w-1/2 shadow-none drop-shadow-none">
                          <FormLabel className="m-0 p-0 text-[14px] text-[#6F7582]">
                            First Name
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Type here" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem className="w-1/2 shadow-none drop-shadow-none">
                          <FormLabel className="m-0 p-0 text-[14px] text-[#6F7582]">
                            Last Name
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Type here" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormBadge
                          label="What is your gender identity?"
                          options={genderOptions}
                          field={field}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="race"
                      render={({ field }) => (
                        <FormSelect
                          label="What is your race? Select all that apply."
                          field={field}
                          placeholder="Select all that apply"
                          options={raceOptions}
                        />
                      )}
                    />
                  </div>
                   <div>
                    <FormField
                      control={form.control}
                      name="identity"
                      render={({ field }) => (
                        <FormBadge
                          label="What identities do you affiliate with? Select all that apply."
                          options={identityOptions}
                          field={field}
                          isMultiValue
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="educationLevel"
                      render={({ field }) => (
                        <FormBadge
                          label="How would you describe yourself?"
                          options={educationLevel}
                          field={field}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="university"
                      render={({ field }) => (
                        <FormComboBox
                          options={UNIVERSITIES}
                          label="What university are you enrolled in?"
                          placeholder="Select or type"
                          field={field}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="workExp"
                      render={({ field }) => (
                        <FormBadge
                          label="What types of work experiences do you have?"
                          options={workExperienceOptions}
                          field={field}
                          isMultiValue
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="discovery"
                      render={({ field }) => (
                        <FormComboBox
                          options={discoveryOptions}
                          label="How did you hear about us?"
                          placeholder="Select or type"
                          field={field}
                        />
                      )}
                    />
                  </div>
                  <div>
                  <FormField
                      control={form.control}
                      name="referral"
                      render={({ field }) => (
                        <FormItem className="w-1/2 shadow-none drop-shadow-none">
                          <FormLabel className="m-0 p-0 text-[14px] text-[#6F7582]">
                            Referral Code
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter referral code here..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className=""></div>
                </>
              )}
              {steps[step] === "STEP-2" && (
                <>
                  <div>
                    <FormField
                      control={form.control}
                      name="interestedRoles"
                      render={({ field }) => (
                        <FormSelect
                          label="What roles are you interested in? Select all that apply."
                          field={field}
                          placeholder="Select all that apply"
                          options={roleOptions}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="wantedRoles"
                      render={({ field }) => (
                        <FormBadge
                          label="How what types of roles are you hoping to secure? Select all that apply."
                          options={wantedRoles}
                          field={field}
                          isMultiValue
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="visaOpts"
                      render={({ field }) => (
                        <FormBadge
                          label="Do you require visa sponsorship?"
                          options={visaOptions}
                          field={field}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="timeSpent"
                      render={({ field }) => (
                        <FormBadge
                          label="Currently, how much time do you spend preparing weekly?"
                          options={timeSpentOptions}
                          field={field}
                        />
                      )}
                    />
                  </div>
                  <div />
                </>
              )}
              {steps[step] === "STEP-3" && (
                <>
                  <div>
                    <FormField
                      control={form.control}
                      name="dayOptions"
                      render={({ field }) => (
                        <FormBadge
                          label="Which days of the week do you want to practice? Select all that apply."
                          options={dayOptions}
                          field={field}
                          isMultiValue
                        />
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="dailyTime"
                      render={({ field }) => (
                        <FormBadge
                          label="On a daily basis, how much time do you want to spend on interview prep?"
                          options={dailyTimeOptions}
                          field={field}
                        />
                      )}
                    />
                  </div>
                </>
              )}
              {steps[step] === "STEP-4" && (
                <>
                  <div className="flex flex-col gap-2 w-full">
                    <h1 className="text-[#6F7582] text-[20px] font-semibold text-left m-0 p-0">
                      1. Share Resume
                    </h1>
                    <div className="flex w-full flex-col">
                      <div className="rounded-t-md bg-[#363F72] p-2 flex gap-2 w-full justify-start items-center">
                        <div className="bg-white rounded-full size-[32px] flex justify-center items-center">
                          <Layers2 color="#3E4784" />
                        </div>
                        <p className="text-[#F8F9FC] font-semibold m-0 p-0">
                          Upload Resume
                        </p>
                      </div>
                      <div className="p-6 border border-[#D5D9EB] rounded-b-[8px]">
                        <UploadFile
                          contentChange={()=>setResume({ file: "", text: "" })}
                          handleFunc={(t: string, f: string, e?:string) => {setResume({ file: f, text: t });}}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 w-full">
                    <h1 className="text-[#6F7582] text-[20px] font-semibold text-left m-0 p-0">
                      2. Add LinkedIn
                    </h1>
                    <FormField
                      control={form.control}
                      name="linkedIn"
                      render={({ field }) => (
                        <FormItem className="w-full shadow-none drop-shadow-none">
                          <FormControl>
                            <Input
                              placeholder="https://www.linkedin.com/in/"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Button ref={buttonRef} type="submit" className="hidden">
                    Submit
                  </Button>
                </>
              )}
            </form>
          </Form>
          }
        </div>
      </div>
    </div>
    }
    </>
  );
};

export default IntakeForm;
